import {
    Button,
    Flex,
    IconWithVariant,
    IconWithVariantProps
} from '@auviknetworks/iceberg-ui';
import { Badge, Typography } from '@mui/material';
import {
    LightTooltip,
    TooltipPlacement
} from 'application/Shared/Components/Tooltips';
import { FontSizes } from 'application/Shared/Constants/SizeArrays';

/*  Shared  */
import { Route } from 'application/Shared/Helpers';
import { Tenant } from 'application/Shared/types/tenant';
import {
    PROVIDER_TYPE_ORDER,
    ProviderType,
    TenantProvider
} from 'application/Shared/types/tenantProvider';
import { TFunction } from 'i18next';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';

const TOOLTIP_STYLES = {
    badge: {
        backgroundColor: 'revert'
    },
    badgeAnchor: {
        backgroundColor: 'revert',
        bottom: '4px',
        right: '-6px'
    },
    container: {
        gap: '10px',
        marginBottom: '8px',
        marginLeft: '16px',
        marginRight: '16px',
        marginTop: '8px'
    }
};

// Helper functions
const getProviderLogo = (
    providerType: ProviderType
): IconWithVariantProps['src'] => {
    const logoMap: Record<ProviderType, IconWithVariantProps['src']> = {
        [ProviderType.Google]: 'Google',
        [ProviderType.Microsoft]: 'Windows',
        [ProviderType.Okta]: 'Okta'
    };
    return logoMap[providerType] || 'BanCircle';
};

const getProviderName = (providerType: ProviderType, t: TFunction): string => {
    const nameMap: Record<ProviderType, string> = {
        [ProviderType.Google]: t('Common.google_workspace'),
        [ProviderType.Microsoft]: t('Common.microsoftEntraID'),
        [ProviderType.Okta]: t('Common.okta')
    };
    return nameMap[providerType] || t('Common.unknown');
};

const getProviderStatus = (provider: TenantProvider) => {
    const { enabled, connectionDetails } = provider;
    return {
        isConnected: enabled && !connectionDetails?.isUpdateRequired,
        isDisconnected: !enabled,
        isUpdateRequired: enabled && connectionDetails?.isUpdateRequired
    };
};

// Components
const LogoIcon = ({
    providerType,
    size
}: {
    providerType: ProviderType;
    size: (typeof FontSizes)[number];
}) => (
    <IconWithVariant
        data-testid={`integrationStatusIcon_${providerType}`}
        variant="primary"
        src={getProviderLogo(providerType)}
        size={size}
    />
);

const StatusBadgeIcon = ({ provider }: { provider: TenantProvider }) => {
    const { isDisconnected, isUpdateRequired } = getProviderStatus(provider);

    let variant: 'success' | 'danger' = 'success';
    let icon: IconWithVariantProps['src'] = 'CheckCircle';

    if (isDisconnected) {
        variant = 'danger';
        icon = 'Disconnected';
    } else if (isUpdateRequired) {
        variant = 'danger';
        icon = 'WarningTriangle';
    }

    return (
        <IconWithVariant
            data-testid={`integrationStatusBadgeIcon_${icon}`}
            variant={variant}
            src={icon}
            size={FontSizes[0]}
            sx={TOOLTIP_STYLES.badge}
        />
    );
};

const ProviderIcon = ({
    provider,
    size = FontSizes[3]
}: {
    provider: TenantProvider;
    size?: (typeof FontSizes)[number];
}) => (
    <Flex sx={{ minWidth: '32px' }}>
        <Badge
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            badgeContent={
                <Flex>
                    <StatusBadgeIcon provider={provider} />
                </Flex>
            }
            overlap="rectangular"
            sx={{
                '& .MuiBadge-anchorOriginBottomRight':
                    TOOLTIP_STYLES.badgeAnchor
            }}
        >
            <LogoIcon providerType={provider.providerType} size={size} />
        </Badge>
    </Flex>
);

const ConfigureIntegrationButton = ({
    id,
    text,
    clientId
}: {
    id: string;
    text: string;
    clientId: string;
}) => {
    const { partnerId } = useParams();
    const baseAdminRoute = Route.buildBaseAdminRoute({
        clientId: clientId!,
        partnerId
    });

    return (
        <Link
            data-trackid="integrationStatus_to_identityManagementIntegration_configure_routeTo"
            to={`${baseAdminRoute}/identityManagement/configuration`}
        >
            <Button
                data-testid="integrationStatusReconnectButton"
                data-trackid={`integrationStatus_identityManagementIntegration_${id}`}
                size="small"
                variant="primary"
            >
                {text}
            </Button>
        </Link>
    );
};

const ProviderTooltipContent = ({
    provider,
    multipleProvidersDisconnected,
    clientId
}: {
    provider: TenantProvider;
    multipleProvidersDisconnected: boolean;
    clientId: string;
}) => {
    const { t } = useTranslation();
    const { isConnected, isDisconnected, isUpdateRequired } =
        getProviderStatus(provider);
    const providerName = getProviderName(provider.providerType, t);

    let statusText = '';
    if (isConnected) {
        statusText = t('Tooltips.providerEnabled', { provider: providerName });
    } else if (isDisconnected) {
        statusText = t('Tooltips.providerDisabled', { provider: providerName });
    } else if (isUpdateRequired) {
        statusText = t('Tooltips.providerUpdateRequired', {
            provider: providerName
        });
    }

    return (
        <Flex alignItems="center" width="fit-content" sx={{ gap: '24px' }}>
            <ProviderIcon provider={provider} size={FontSizes[4]} />
            <Flex sx={{ alignItems: 'center', gap: '24px' }}>
                <Flex flexDirection="column">
                    <Typography variant="body1" color="text.secondary">
                        {statusText}
                    </Typography>
                    {!multipleProvidersDisconnected && isDisconnected && (
                        <Typography variant="body1" color="text.secondary">
                            {t('Tooltips.providerReconnect')}
                        </Typography>
                    )}
                </Flex>
                {!multipleProvidersDisconnected && isDisconnected && (
                    <ConfigureIntegrationButton
                        id="reconnect"
                        text={t('Common.reconnect')}
                        clientId={clientId}
                    />
                )}
            </Flex>
        </Flex>
    );
};

const TooltipContent = ({
    providers,
    clientId,
    multipleProvidersDisconnected
}: {
    providers: TenantProvider[];
    clientId: string;
    multipleProvidersDisconnected: boolean;
}) => {
    const { t } = useTranslation();

    return (
        <Flex alignItems="center">
            <Flex
                alignItems="flex-start"
                flexDirection="column"
                sx={TOOLTIP_STYLES.container}
            >
                {providers.map((provider) => (
                    <ProviderTooltipContent
                        key={provider.id}
                        provider={provider}
                        clientId={clientId}
                        multipleProvidersDisconnected={
                            multipleProvidersDisconnected
                        }
                    />
                ))}
                {multipleProvidersDisconnected && (
                    <Typography variant="body1" color="text.secondary">
                        {t('Tooltips.providerReconnect')}
                    </Typography>
                )}
                {!providers.length && (
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        dangerouslySetInnerHTML={{
                            __html: t('Tooltips.providerNoIntegrationMessage')
                        }}
                    />
                )}
            </Flex>
            {multipleProvidersDisconnected && (
                <ConfigureIntegrationButton
                    id="reconnect"
                    text={t('Common.reconnect')}
                    clientId={clientId}
                />
            )}
            {!providers.length && (
                <ConfigureIntegrationButton
                    id="configure"
                    text={t('Common.configure')}
                    clientId={clientId}
                />
            )}
        </Flex>
    );
};

const sortProviders = (providers: TenantProvider[]): TenantProvider[] =>
    providers && providers.length
        ? [...providers].sort(
              (a, b) =>
                  PROVIDER_TYPE_ORDER.indexOf(a.providerType) -
                  PROVIDER_TYPE_ORDER.indexOf(b.providerType)
          )
        : [];

// Main component
export const IntegrationStatus = ({
    providers,
    tenant
}: {
    providers: TenantProvider[];
    tenant: Tenant;
}) => {
    const { t } = useTranslation();
    const sortedProviders = sortProviders(providers);
    const multipleProvidersDisconnected =
        sortedProviders.length > 1 &&
        sortedProviders.filter((provider) => !provider.enabled).length > 1;

    return (
        <Flex width="100%">
            <LightTooltip
                arrow
                placement={TooltipPlacement.RIGHT}
                slotProps={{
                    tooltip: {
                        sx: { minWidth: 'fit-content' }
                    }
                }}
                title={
                    <TooltipContent
                        providers={sortedProviders}
                        clientId={tenant.id}
                        multipleProvidersDisconnected={
                            multipleProvidersDisconnected
                        }
                    />
                }
            >
                <Flex
                    width="fit-content"
                    sx={{ gap: '8px' }}
                    data-testid="integrationStatusTooltip"
                >
                    {sortedProviders.map((provider) => (
                        <ProviderIcon key={provider.id} provider={provider} />
                    ))}
                    {!sortedProviders.length && (
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ cursor: 'default' }}
                        >
                            {t(
                                'PartnerOrganization.integrationStatus.noIntegration'
                            )}
                        </Typography>
                    )}
                </Flex>
            </LightTooltip>
        </Flex>
    );
};
